import React, { useState, useEffect } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  TextField,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Autocomplete,
  Typography,
  Collapse,
  Divider,
  Chip,
  useMediaQuery,
  useTheme,
  alpha,
} from "@mui/material";

import { useNavigate } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import { getAllEnquiries, deleteEnquiry, updateEnquiry } from "../../../Services/Services";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Print from "@mui/icons-material/Print";
import { Tooltip } from "@mui/material";
import { handlePrint } from "./EnquiryPrint";

const EnquiryList = () => {
  const [enquiries, setEnquiries] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [enquiryToDelete, setEnquiryToDelete] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [enquiryToEdit, setEnquiryToEdit] = useState(null);
  const [customerSearch, setCustomerSearch] = useState("");
  const [companySearch, setCompanySearch] = useState("");
  const [expandedEnquiry, setExpandedEnquiry] = useState(null);

  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const tableHeadBg = isDark ? "#252b38" : "#f0f4ff";

  const rowBg = (index) =>
    isDark
      ? index % 2 === 0
        ? "#20242e"
        : "#1a1d26"
      : index % 2 === 0
      ? "#fafafa"
      : "#ffffff";

  const rowHoverBg = isDark ? "#2c3242" : "#e3f2fd";

  // Safe Date Formatter (Prevents rendering crashes)
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      const date = new Date(dateStr);
      return isNaN(date.getTime()) ? "-" : date.toLocaleDateString("en-GB");
    } catch {
      return "-";
    }
  };

  const fetchEnquiriesList = async () => {
    try {
      const data = await getAllEnquiries();
      setEnquiries(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to load enquiries:", err);
      toast.error("Failed to load enquiries");
    }
  };

  useEffect(() => {
    fetchEnquiriesList();
  }, []);

  useEffect(() => {
    document.body.style.overflow = isMobile ? "auto" : "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isMobile]);

  const handleEditClick = (enquiry) => {
    setEnquiryToEdit({ ...enquiry });
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async () => {
    if (!enquiryToEdit || !enquiryToEdit.enquiry_id) return;

    const response = await updateEnquiry(
      enquiryToEdit.enquiry_id,
      enquiryToEdit
    );

    if (response) {
      toast.success("Enquiry updated successfully!");
      fetchEnquiriesList();
      setEditDialogOpen(false);
    } else {
      toast.error("Failed to update enquiry.");
    }
  };

  const statusOptions = [
    "New",
    "Under Review",
    "Quoted",
    "Converted",
    "Closed - Lost",
  ];

  const enquirySources = [
    "Website",
    "Phone Call",
    "Email",
    "Sales Visit",
    "Reference",
    "Other",
  ];

  const confirmDelete = async () => {
    if (!enquiryToDelete || !enquiryToDelete.enquiry_id) {
      toast.error("Invalid enquiry ID. Cannot delete.");
      return;
    }
    const response = await deleteEnquiry(enquiryToDelete.enquiry_id);
    if (response) {
      toast.success("Enquiry deleted successfully!");
      fetchEnquiriesList();
    } else {
      toast.error("Failed to delete enquiry.");
    }
    setDeleteDialogOpen(false);
    setEnquiryToDelete(null);
  };

  const filteredEnquiries = enquiries.filter(
    (enq) =>
      (enq.customer_name || "")
        .toLowerCase()
        .includes(customerSearch.toLowerCase()) &&
      (enq.company_name || "")
        .toLowerCase()
        .includes(companySearch.toLowerCase())
  );

  const handleDeleteClick = (enquiry) => {
    setEnquiryToDelete(enquiry);
    setDeleteDialogOpen(true);
  };

  const truncateText = (text, limit = 20) => {
    if (!text) return "";
    return text.length > limit ? text.substring(0, limit) + "..." : text;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "New":
        return "#2196f3";
      case "Under Review":
        return "#ff9800";
      case "Quoted":
        return "#9185a1";
      case "Converted":
        return "#4caf50";
      case "Closed - Lost":
        return "#f44336";
      default:
        return "#757575";
    }
  };

  const dialogPaperSx = {
    bgcolor: "background.paper",
    backgroundImage: "none",
    border: "1px solid",
    borderColor: "divider",
    boxShadow: isDark
      ? "0 12px 40px rgba(0,0,0,0.65)"
      : "0 8px 32px rgba(0,0,0,0.14)",
  };

  return (
    <Box
      sx={{
        px: 0,
        pt: 0,
        mt: { xs: 3, md: -3 },
        maxWidth: 1200,
        mx: "auto",
        bgcolor: "background.default",
        minHeight: "100%",
      }}
    >
      <ToastContainer
        position="top-right"
        autoClose={3000}
        theme={isDark ? "dark" : "light"}
      />

      {/* ── FILTERS BAR ── */}
      <Box
        sx={{
          mb: 2,
          position: "sticky",
          top: 0,
          zIndex: 1100,
          pt: 1,
          pb: 1,
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: (t) => alpha(t.palette.background.paper, 0.95),
          backdropFilter: "blur(8px)",
          boxShadow: isDark
            ? "0 2px 10px rgba(0,0,0,0.45)"
            : "0 1px 4px rgba(0,0,0,0.07)",
        }}
      >
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={12} sm={5}>
            <TextField
              fullWidth
              label="Search by Customer"
              size="small"
              value={customerSearch}
              onChange={(e) => setCustomerSearch(e.target.value)}
              sx={{
                "& .MuiInputBase-root": { height: 32, borderRadius: 0 },
                "& .MuiInputLabel-root": { fontSize: "0.7rem" },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={5}>
            <TextField
              fullWidth
              label="Search by Company"
              size="small"
              value={companySearch}
              onChange={(e) => setCompanySearch(e.target.value)}
              sx={{
                "& .MuiInputBase-root": { height: 32, borderRadius: 0 },
                "& .MuiInputLabel-root": { fontSize: "0.7rem" },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={2}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={() => navigate("/dashboard/Createenquiry")}
              startIcon={<AddIcon />}
              sx={{ height: 32, fontSize: "0.7rem", borderRadius: 0 }}
            >
              Enquiry
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* ── MOBILE VIEW (CARDS) ── */}
      {isMobile ? (
        <Box sx={{ bgcolor: "background.paper" }}>
          {filteredEnquiries.map((e, index) => (
            <Box key={e.enquiry_id || index}>
              <Box
                onClick={() =>
                  setExpandedEnquiry(
                    expandedEnquiry === e.enquiry_id ? null : e.enquiry_id
                  )
                }
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  p: 1.5,
                  cursor: "pointer",
                  bgcolor: rowBg(index),
                  "&:active": { bgcolor: "action.selected" },
                  borderLeft: `4px solid ${getStatusColor(e.status)}`,
                  transition: "background-color 0.15s ease",
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 0.5,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.7rem",
                        color: "text.disabled",
                        minWidth: 25,
                      }}
                    >
                      #{index + 1}
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: "bold",
                        fontSize: "0.85rem",
                        flex: 1,
                        color: "text.primary",
                      }}
                    >
                      {e.customer_name}
                    </Typography>
                    <Chip
                      label={e.status || "New"}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: "0.65rem",
                        bgcolor: getStatusColor(e.status),
                        color: "#fff",
                        "& .MuiChip-label": { px: 1 },
                      }}
                    />
                  </Box>

                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      color: "text.secondary",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <span style={{ fontWeight: 500 }}>
                      {(e.company_name || "").substring(0, 20)}
                    </span>
                    <span>•</span>
                    <span style={{ fontSize: "0.7rem" }}>{e.phone}</span>
                  </Typography>
                </Box>

                <IconButton size="small">
                  {expandedEnquiry === e.enquiry_id ? (
                    <ExpandLess fontSize="small" />
                  ) : (
                    <ExpandMore fontSize="small" />
                  )}
                </IconButton>
              </Box>

              <Collapse
                in={expandedEnquiry === e.enquiry_id}
                timeout="auto"
                unmountOnExit
              >
                <Box
                  sx={{
                    px: 1.5,
                    py: 1,
                    bgcolor: (t) =>
                      t.palette.mode === "dark"
                        ? alpha(t.palette.common.white, 0.05)
                        : alpha(t.palette.primary.main, 0.04),
                    borderLeft: `4px solid ${getStatusColor(e.status)}`,
                  }}
                >
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 0.8,
                      mb: 1,
                    }}
                  >
                    {[
                      { label: "EMAIL", value: e.email },
                      { label: "CITY", value: e.city },
                      { label: "ENQUIRY TYPE", value: e.enquiry_source },
                      {
                        label: "FOLLOW-UP",
                        value: formatDate(e.follow_up_date),
                      },
                    ].map(({ label, value }) => (
                      <Box key={label}>
                        <Typography
                          sx={{
                            fontSize: "0.6rem",
                            color: "text.disabled",
                            textTransform: "uppercase",
                            letterSpacing: "0.08em",
                            fontWeight: 600,
                          }}
                        >
                          {label}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.72rem",
                            fontWeight: 500,
                            color: "text.secondary",
                            wordBreak: "break-word",
                          }}
                        >
                          {value || "-"}
                        </Typography>
                      </Box>
                    ))}
                  </Box>

                  {e.notes && e.notes.length > 0 && (
                    <Box sx={{ mb: 0.8 }}>
                      <Typography
                        sx={{
                          fontSize: "0.6rem",
                          color: "text.disabled",
                          textTransform: "uppercase",
                          letterSpacing: "0.08em",
                          fontWeight: 600,
                          mb: 0.3,
                        }}
                      >
                        NOTES
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.7rem",
                          color: "text.secondary",
                          fontStyle: "italic",
                          lineHeight: 1.3,
                        }}
                      >
                        {Array.isArray(e.notes)
                          ? e.notes.map((n) => n.note_text).join(" | ")
                          : e.notes}
                      </Typography>
                    </Box>
                  )}

                  <Divider sx={{ my: 1, borderColor: "divider" }} />

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: 0.6,
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleEditClick(e);
                      }}
                      sx={{
                        bgcolor: (t) =>
                          t.palette.mode === "dark"
                            ? alpha(t.palette.primary.main, 0.15)
                            : "#e3f2fd",
                        borderRadius: "6px",
                        width: 32,
                        height: 32,
                      }}
                    >
                      <EditIcon sx={{ fontSize: "1rem", color: "primary.main" }} />
                    </IconButton>

                    <IconButton
                      size="small"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleDeleteClick(e);
                      }}
                      sx={{
                        bgcolor: (t) =>
                          t.palette.mode === "dark"
                            ? alpha(t.palette.error.main, 0.15)
                            : "#ffebee",
                        borderRadius: "6px",
                        width: 32,
                        height: 32,
                      }}
                    >
                      <DeleteIcon sx={{ fontSize: "1rem", color: "error.main" }} />
                    </IconButton>

                    <IconButton
                      size="small"
                      onClick={(event) => {
                        event.stopPropagation();
                        handlePrint(e);
                      }}
                      sx={{
                        bgcolor: (t) =>
                          t.palette.mode === "dark"
                            ? alpha(t.palette.info.main, 0.15)
                            : "#e1f5fe",
                        borderRadius: "6px",
                        width: 32,
                        height: 32,
                      }}
                    >
                      <Print sx={{ fontSize: "1rem", color: "info.main" }} />
                    </IconButton>
                  </Box>
                </Box>
              </Collapse>

              <Divider />
            </Box>
          ))}
        </Box>
      ) : (
        /* ── DESKTOP TABLE VIEW ── */
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            maxHeight: "88vh",
            overflow: "auto",
            bgcolor: "background.paper",
            backgroundImage: "none",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 0,
            boxShadow: isDark ? "0 4px 18px rgba(0,0,0,0.35)" : "none",
          }}
        >
          <Table
            stickyHeader
            size="small"
            sx={{
              minWidth: 1600,
              "& td, & th": {
                textAlign: "center",
                whiteSpace: "nowrap",
                borderBottom: "1px solid",
                borderColor: "divider",
              },
            }}
          >
            <TableHead
              sx={{
                "& th": {
                  fontWeight: "bold",
                  fontSize: "0.8rem",
                  color: "text.primary",
                  backgroundColor: `${tableHeadBg} !important`,
                  borderBottom: "2px solid",
                  borderColor: "primary.main",
                  letterSpacing: "0.04em",
                },
              }}
            >
              <TableRow>
                <TableCell
                  sx={{
                    position: "sticky",
                    left: 0,
                    backgroundColor: `${tableHeadBg} !important`,
                    zIndex: 3,
                  }}
                >
                  S.No.
                </TableCell>
                <TableCell>Contact Person</TableCell>
                <TableCell>Company</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>City</TableCell>
                <TableCell>Country</TableCell>
                <TableCell>Enquiry Type</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Followup Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell
                  sx={{
                    position: "sticky",
                    right: 0,
                    backgroundColor: `${tableHeadBg} !important`,
                    zIndex: 3,
                  }}
                >
                  Action
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredEnquiries.map((e, idx) => (
                <TableRow
                  key={e.enquiry_id || idx}
                  sx={{
                    backgroundColor: rowBg(idx),
                    transition: "background-color 0.15s ease",
                    "&:hover": {
                      backgroundColor: `${rowHoverBg} !important`,
                      "& td": {
                        backgroundColor: "inherit !important",
                      },
                    },
                    "& td": {
                      fontSize: "0.75rem",
                      padding: "0px 13px",
                      color: "text.primary",
                    },
                  }}
                >
                  <TableCell
                    sx={{
                      position: "sticky",
                      left: 0,
                      bgcolor: "inherit",
                      zIndex: 2,
                    }}
                  >
                    {idx + 1}
                  </TableCell>
                  <TableCell>{e.customer_name || "-"}</TableCell>
                  <TableCell>{e.company_name || "-"}</TableCell>
                  <TableCell>
                    <Tooltip title={e.email || "-"} arrow>
                      <span>{truncateText(e.email, 20)}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>{e.phone || "-"}</TableCell>
                  <TableCell>{e.city || "-"}</TableCell>
                  <TableCell>{e.country || "-"}</TableCell>
                  <TableCell>{e.enquiry_source || "-"}</TableCell>
                  <TableCell>{formatDate(e.created_at)}</TableCell>
                  <TableCell>{formatDate(e.follow_up_date)}</TableCell>

                  <TableCell>
                    <Chip
                      label={e.status || "New"}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: "0.65rem",
                        bgcolor: getStatusColor(e.status),
                        color: "#fff",
                        "& .MuiChip-label": { px: 1 },
                      }}
                    />
                  </TableCell>

                  <TableCell
                    sx={{
                      position: "sticky",
                      right: 0,
                      bgcolor: "inherit",
                      zIndex: 2,
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={() =>
                        navigate("/dashboard/Createenquiry", {
                          state: { data: e },
                        })
                      }
                      color="primary"
                      sx={{ padding: 0, mx: 0.5 }}
                    >
                      <EditIcon transform="scale(0.6)" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteClick(e)}
                      color="error"
                      sx={{ padding: 0, mx: 0.5 }}
                    >
                      <DeleteIcon transform="scale(0.6)" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handlePrint(e)}
                      color="info"
                      sx={{ padding: 0, mx: 0.5 }}
                    >
                      <Print transform="scale(0.6)" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ── DELETE DIALOG ── */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle
          sx={{
            borderBottom: "1px solid",
            borderColor: "divider",
            color: "text.primary",
            fontWeight: 700,
            bgcolor: (t) =>
              t.palette.mode === "dark"
                ? alpha(t.palette.error.main, 0.12)
                : alpha(t.palette.error.main, 0.06),
          }}
        >
          Confirm Delete
        </DialogTitle>
        <DialogContent sx={{ pt: 2, color: "text.primary" }}>
          Are you sure you want to delete enquiry from{" "}
          <strong>{enquiryToDelete?.customer_name}</strong>?
        </DialogContent>
        <DialogActions
          sx={{
            borderTop: "1px solid",
            borderColor: "divider",
            px: 2,
            py: 1,
            bgcolor: "background.paper",
          }}
        >
          <Button
            variant="outlined"
            size="small"
            onClick={() => setDeleteDialogOpen(false)}
          >
            Close
          </Button>
          <Button
            color="error"
            variant="contained"
            size="small"
            onClick={confirmDelete}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── EDIT DIALOG ── */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle
          sx={{
            borderBottom: "1px solid",
            borderColor: "divider",
            color: "text.primary",
            fontWeight: 700,
            fontSize: "0.95rem",
            bgcolor: (t) =>
              t.palette.mode === "dark"
                ? alpha(t.palette.primary.main, 0.15)
                : alpha(t.palette.primary.main, 0.06),
          }}
        >
          Edit Enquiry
        </DialogTitle>

        <DialogContent dividers sx={{ bgcolor: "background.paper" }}>
          <Box component={Grid} container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Contact Person"
                fullWidth
                value={enquiryToEdit?.customer_name || ""}
                onChange={(e) =>
                  setEnquiryToEdit({
                    ...enquiryToEdit,
                    customer_name: e.target.value,
                  })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Company"
                fullWidth
                value={enquiryToEdit?.company_name || ""}
                onChange={(e) =>
                  setEnquiryToEdit({
                    ...enquiryToEdit,
                    company_name: e.target.value,
                  })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Email"
                fullWidth
                value={enquiryToEdit?.email || ""}
                onChange={(e) =>
                  setEnquiryToEdit({
                    ...enquiryToEdit,
                    email: e.target.value,
                  })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Phone"
                fullWidth
                value={enquiryToEdit?.phone || ""}
                onChange={(e) =>
                  setEnquiryToEdit({
                    ...enquiryToEdit,
                    phone: e.target.value,
                  })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="City"
                fullWidth
                value={enquiryToEdit?.city || ""}
                onChange={(e) =>
                  setEnquiryToEdit({
                    ...enquiryToEdit,
                    city: e.target.value,
                  })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                fullWidth
                options={enquirySources}
                value={enquiryToEdit?.enquiry_source || ""}
                onChange={(e, newValue) =>
                  setEnquiryToEdit({
                    ...enquiryToEdit,
                    enquiry_source: newValue || "",
                  })
                }
                renderInput={(params) => (
                  <TextField {...params} label="Enquiry Type" required />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Followup Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                fullWidth
                value={enquiryToEdit?.follow_up_date || ""}
                onChange={(e) =>
                  setEnquiryToEdit({
                    ...enquiryToEdit,
                    follow_up_date: e.target.value,
                  })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={statusOptions}
                value={enquiryToEdit?.status || ""}
                onChange={(e, newValue) =>
                  setEnquiryToEdit({
                    ...enquiryToEdit,
                    status: newValue || "",
                  })
                }
                renderInput={(params) => (
                  <TextField {...params} label="Status" fullWidth />
                )}
              />
            </Grid>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            borderTop: "1px solid",
            borderColor: "divider",
            px: 2,
            py: 1,
            bgcolor: "background.paper",
          }}
        >
          <Button
            variant="outlined"
            size="small"
            onClick={() => setEditDialogOpen(false)}
          >
            Close
          </Button>
          <Button
            onClick={handleEditSubmit}
            color="primary"
            variant="contained"
            size="small"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EnquiryList;